import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Centered from '../PageLayout/Centered';
import GlobalTheme from '../../theme';

const ID_STICKY_PORTAL = 'sticky-portal';

/**
 * Portal for the sticky header tabs
 */
export const StickyPortal = () => (
  <div
    id={ID_STICKY_PORTAL}
    style={{
      position: 'sticky',
      width: '100%',
      top: 0,
      zIndex: GlobalTheme.zIndex.drawer,
    }}
  />
);

interface StickyProps {
  children: React.ReactElement;
  /**
   * The ref of the element to observe for visibility
   */
  observedRef?: React.RefObject<HTMLDivElement>;
  /**
   * className to be applied to the sticky container for children
   */
  className?: string;
}

/**
 * @description This component will make its children sticky when the observedRef is no longer visible
 */
export default function Sticky({
  children,
  observedRef,
  className,
}: StickyProps) {
  const [isSticky, setIsSticky] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries =>
        entries.forEach(entry => {
          setIsSticky(!entry.isIntersecting);
        }),
      { threshold: 0.1 }
    );

    const ref = observedRef?.current;
    if (ref) {
      observer.observe(ref);
    }

    return () => {
      if (ref) {
        observer.unobserve(ref);
      }
    };
  }, [observedRef]);

  const destination = document.getElementById(ID_STICKY_PORTAL);

  return isSticky && destination
    ? createPortal(
        <Centered className={className}>{children}</Centered>,
        destination
      )
    : children;
}
